import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy, Check, Link2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { normalizeLanguageCode } from '@/i18n/languages';
import { copyWithFeedback } from '../../utils/clipboardUtils';
import { supabase } from '@/lib/supabaseGuard';
import { generalInvitationService } from '@/services/friends/generalInvitationService';
import { invitationCacheService } from '@/services/friends/invitationCacheService';
import { renderInviteMessage } from '@/lib/invitations/templates';
import { BrandLogo } from '@/components/BrandLogo';

const AddFriendsModal = ({ isOpen, onClose, user }) => {
  const { t, i18n } = useTranslation();
  const [copyStatus, setCopyStatus] = useState('idle'); // idle, copying, success, error
  const [inviteLink, setInviteLink] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserInviteLink() {
      if (!isOpen) {
        setLoading(false);
        return;
      }

      console.log('🔗 V177: Fetching user invite link and message');
      setLoading(true);

      try {
        const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !currentUser) {
          console.error('❌ V177: Auth error or no authenticated user:', authError);
          return;
        }

        const lang = normalizeLanguageCode(i18n.language) ?? 'en';

        const cachedInvitation = invitationCacheService.getCachedInvitation(currentUser.id);
        if (cachedInvitation) {
          console.log('✅ V177: Using cached invitation data:', cachedInvitation.inviteCode);
          setInviteLink(cachedInvitation.inviteLink);
          const message = await generalInvitationService.getInvitationShareMessage(
            cachedInvitation.inviteCode,
            lang
          );
          setInviteMessage(message || cachedInvitation.inviteMessage);
          return;
        }

        console.log('🔄 V177: Cache miss, loading from database...');
        const { data: invitations, error } = await supabase
          .from('general_invitations')
          .select('invite_code, max_uses, current_uses')
          .eq('creator_id', currentUser.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1);

        const invitation = Array.isArray(invitations) && invitations.length > 0 ? invitations[0] : null;

        if (error) {
          console.error('❌ V177: Error fetching invite link:', error);
          const fallbackMessage = await generalInvitationService.getInvitationShareMessageByUserId(
            currentUser.id,
            lang
          );
          if (fallbackMessage) {
            setInviteMessage(fallbackMessage);
            const linkMatch = fallbackMessage.match(/https:\/\/blissnet\.app\/invite\/([a-z0-9-]+)/i);
            if (linkMatch) setInviteLink(linkMatch[0]);
          }
          return;
        }

        if (invitation) {
          const link = `https://blissnet.app/invite/${invitation.invite_code}`;
          setInviteLink(link);

          const message = await generalInvitationService.getInvitationShareMessage(
            invitation.invite_code,
            lang
          );
          setInviteMessage(message || '');
          console.log('✅ V177: Invite link and message loaded:', link);
          return;
        }

        console.warn('⚠️ V177: No invite in DB, loading via cache service');
        const cachedData = await invitationCacheService.loadAndCacheInvitation(currentUser.id);
        if (cachedData) {
          setInviteLink(cachedData.inviteLink);
          const message = await generalInvitationService.getInvitationShareMessage(
            cachedData.inviteCode,
            lang
          );
          setInviteMessage(message || cachedData.inviteMessage);
        }
      } catch (error) {
        console.error('💥 V177: Exception fetching invite link:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchUserInviteLink();
  }, [isOpen, i18n.language]);

  const handleDirectCopy = async () => {
    if (!canonicalInviteMessage) return;
    setCopyStatus('copying');

    await copyWithFeedback(
      canonicalInviteMessage,
      (method) => {
        console.log('✅ Copy successful via:', method);
        setCopyStatus('success');
        setTimeout(() => setCopyStatus('idle'), 2000);
      },
      (error) => {
        console.log('ℹ️ Manual copy required:', error);
        setCopyStatus('error');
        setTimeout(() => setCopyStatus('idle'), 3000);
      }
    );
  };

  const handleCopyLinkOnly = async () => {
    if (!inviteLink) return;
    await copyWithFeedback(
      inviteLink,
      () => toast.success(t('invite.linkCopied')),
      (err) => toast.error(err || 'Failed to copy link')
    );
  };

  const handleWhatsAppShare = async () => {
    const text = canonicalInviteMessage;
    if (!text) return;

    const encodedText = encodeURIComponent(text);
    const isNative = Capacitor.isNativePlatform();
    const platform = isNative ? Capacitor.getPlatform() : 'web';
    const isNativeIOS = isNative && platform === 'ios';
    const isNativeAndroid = isNative && platform === 'android';

    const tryNativeWhatsAppHandoff = async (): Promise<boolean> => {
      if (!isNativeIOS && !isNativeAndroid) return false;

      return await new Promise<boolean>((resolve) => {
        let settled = false;

        const cleanup = () => {
          document.removeEventListener('visibilitychange', onVisibilityChange, true);
          window.clearTimeout(timeoutId);
        };

        const finish = (result: boolean) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(result);
        };

        const onVisibilityChange = () => {
          if (document.visibilityState === 'hidden') {
            finish(true);
          }
        };

        const timeoutId = window.setTimeout(() => finish(false), 900);
        document.addEventListener('visibilitychange', onVisibilityChange, true);

        try {
          window.location.href = `whatsapp://send?text=${encodedText}`;
        } catch {
          finish(false);
        }
      });
    };

    // iOS native path: preserve current working behavior.
    if (isNativeIOS) {
      const isIphoneDevice = /iPhone/i.test(navigator.userAgent);
      const isSimulatorLike = /Simulator/i.test(navigator.userAgent);
      if (isIphoneDevice && !isSimulatorLike) {
        const handedOffToApp = await tryNativeWhatsAppHandoff();
        if (handedOffToApp) return;
      }

      if (navigator.share) {
        try {
          await navigator.share({ text });
          return;
        } catch {
          // Continue to clipboard fallback.
        }
      }
    }

    // Android native path: restore dedicated pre-iOS-fix WhatsApp behavior.
    if (isNativeAndroid) {
      const isSimulatorLike = /Simulator|Emulator/i.test(navigator.userAgent);
      if (!isSimulatorLike) {
        const handedOffToApp = await tryNativeWhatsAppHandoff();
        if (handedOffToApp) return;
      }

      const opened = window.open(`https://api.whatsapp.com/send?text=${encodedText}`, '_blank');
      if (opened) return;
    }

    // Web/desktop path: keep current generic share fallback.
    if (!isNative && navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch {
        // Continue to clipboard fallback.
      }
    }

    let copied = false;
    await copyWithFeedback(
      text,
      () => {
        copied = true;
        toast.success('Invite copied. Open WhatsApp and paste the message.');
      },
      () => {
        copied = false;
      }
    );

    if (!copied) {
      toast.error('Could not share or copy invite. Please try again.');
    }
  };

  const canonicalInviteMessage =
    (inviteMessage || '').trim() || (inviteLink ? renderInviteMessage({ inviteLink, templateBody: null }) : '');
  const isIOSPlatform = Capacitor.getPlatform() === 'ios';
  const modalWrapperStyle = isIOSPlatform
    ? {
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      }
    : undefined;
  const modalCardStyle = isIOSPlatform
    ? {
        maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px)',
      }
    : undefined;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 overflow-y-auto"
      style={modalWrapperStyle}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        style={modalCardStyle}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E8E0DA] dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600/10 rounded-lg flex items-center justify-center">
              <BrandLogo size={24} className="rounded-md" alt="BlissNet logo" />
            </div>
            <h2 className="text-xl font-bold text-[#2D2A26] dark:text-white">{t('invite.title')}</h2>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleWhatsAppShare}
              className="p-2 bg-green-600 hover:bg-green-500 rounded-lg transition-colors"
              title={t('invite.shareWhatsApp')}
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.04 2c5.5 0 9.96 4.45 9.96 9.96 0 1.76-.46 3.47-1.33 4.97l1.33 4.88-5.05-1.32a9.94 9.94 0 0 1-4.91 1.26C6.5 21.75 2 17.3 2 11.8 2 6.3 6.5 2 12.04 2zm-.04 2C7.04 4 3.5 7.59 3.5 11.8c0 2.28 1.01 4.4 2.72 5.9l-.77 2.84 2.93-.77a8.04 8.04 0 0 0 3.61.87c4.99 0 8.54-3.59 8.54-7.8 0-4.21-3.55-7.8-8.54-7.8zm3.58 10.6c-.2-.11-1.17-.57-1.35-.64-.18-.07-.31-.1-.44.1-.13.2-.5.63-.61.76-.11.13-.22.15-.42.04-.2-.1-.83-.31-1.58-.98a5.94 5.94 0 0 1-1.1-1.36c-.12-.21-.01-.32.09-.43.09-.1.2-.24.3-.35.1-.11.13-.2.2-.33.07-.13.03-.25-.02-.35-.06-.1-.44-1.06-.6-1.45-.16-.39-.33-.34-.44-.34h-.38c-.12 0-.3.04-.45.2-.15.15-.6.58-.6 1.4 0 .82.61 1.61.7 1.72.09.12 1.2 1.83 2.9 2.57 1.7.74 1.7.5 2.01.47.3-.03.98-.39 1.12-.77.13-.39.13-.72.1-.77-.02-.05-.17-.1-.36-.2z" />
              </svg>
            </button>

            <button
              onClick={handleDirectCopy}
              disabled={copyStatus === 'copying'}
              className={`p-2 rounded-lg transition-colors ${copyStatus === 'success'
                  ? 'bg-green-600 text-white'
                  : copyStatus === 'error'
                    ? 'bg-red-600 text-white'
                    : copyStatus === 'copying'
                      ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
              title={copyStatus === 'success' ? t('invite.linkCopied') : copyStatus === 'error' ? t('common.retry') : t('invite.copyMessage')}
            >
              {copyStatus === 'success' ? (
                <Check size={20} />
              ) : copyStatus === 'copying' ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Copy size={20} />
              )}
            </button>

            <button onClick={onClose} className="p-2 hover:bg-[#F8F4F1] dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-[#9B9490] dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <div className="bg-[#F8F4F1] dark:bg-gray-800/50 rounded-xl p-5 mb-6 border border-[#E8E0DA] dark:border-gray-700">
            <h3 className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-2">
              <span>📋</span> {t('invite.previewMessage')}
            </h3>

            {loading ? (
              <div className="flex items-center gap-2 text-[#9B9490] dark:text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>{t('invite.loadingInviteMessage')}</span>
              </div>
            ) : canonicalInviteMessage ? (
              <div className="text-[#2D2A26] dark:text-gray-100 text-sm leading-relaxed whitespace-pre-wrap" dir="auto">
                {canonicalInviteMessage}
              </div>
            ) : (
              <div className="text-[#9B9490] dark:text-gray-400 text-sm">{t('invite.noInviteMessage')}</div>
            )}
          </div>

          {/* V328.2: Copy Link Only - secondary action */}
          <button
            onClick={handleCopyLinkOnly}
            disabled={loading || !inviteLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#E8E0DA] dark:border-gray-600 text-sm text-[#2D2A26] dark:text-gray-200 hover:bg-[#F8F4F1] dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-6"
          >
            <Link2 className="w-4 h-4" />
            {t('invite.copyLinkOnly')}
          </button>

          {/* V161.4: Policy-aligned reward section - trust-focused, no hype */}
          <div className="bg-[#F8F4F1] dark:bg-gray-800/50 rounded-xl p-5 mb-6 border border-[#E8E0DA] dark:border-gray-700">
            <h3 className="text-sm font-semibold text-[#2D2A26] dark:text-white mb-2">
              {t('invite.rewardsTitle')}
            </h3>
            <p className="text-sm text-[#6B6560] dark:text-gray-300 leading-relaxed mb-4">
              {t('invite.rewardsDescription')}
            </p>
            <Link
              to="/legal/wedding-gift"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline underline-offset-2"
              onClick={onClose}
            >
              {t('invite.viewPolicyEligibility')}
            </Link>
          </div>

          {copyStatus !== 'idle' && (
            <div
              className={`p-3 rounded-lg text-sm font-medium border ${copyStatus === 'success'
                  ? 'bg-green-600/20 text-green-400 border-green-500/30'
                  : copyStatus === 'error'
                    ? 'bg-amber-600/20 text-amber-400 border-amber-500/30'
                    : 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                }`}
            >
              {copyStatus === 'success' && t('invite.messageCopied')}
              {copyStatus === 'error' && t('invite.copyManually')}
              {copyStatus === 'copying' && t('invite.copying')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFriendsModal;
